import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";
import { CUSTOMER_STORIES } from "@/data/content";

export const CustomerStories = ({ openDemo, hideHeader = false }) => {
    const [active, setActive] = useState(0);
    const c = CUSTOMER_STORIES[active];

    return (
        <section className="py-24 md:py-28 bg-clean" data-testid="customer-stories">
            <div className="container-x">
                {!hideHeader && (
                    <Reveal>
                        <div className="mb-12 max-w-3xl">
                            <div className="inline-flex items-center gap-2 text-xs font-medium tracking-widest uppercase text-slate-500 mb-4">
                                <span className="w-6 h-px bg-slate-400" /> Customer stories
                            </div>
                            <h2 className="heading-section text-3xl md:text-5xl">
                                Real deployments. <span className="italic">Measurable outcomes.</span>
                            </h2>
                            <p className="mt-5 text-intro text-slate-600 text-base md:text-lg">
                                From distance-learning counselling on voice to fintech credit resolution on
                                WhatsApp — Nyra AI is already at work behind the brands millions of Indians trust.
                            </p>
                        </div>
                    </Reveal>
                )}

                {/* ---- Story selector tabs ---- */}
                <Reveal>
                    <div className="flex flex-col sm:flex-row gap-3 mb-8" role="tablist" data-testid="story-tabs">
                        {CUSTOMER_STORIES.map((s, i) => {
                            const isActive = i === active;
                            return (
                                <button
                                    key={s.slug}
                                    role="tab"
                                    aria-selected={isActive}
                                    onClick={() => setActive(i)}
                                    className="group relative flex-1 flex items-center gap-4 text-left rounded-2xl p-4 md:p-5 border transition-all duration-300"
                                    style={{
                                        borderColor: isActive ? `${s.accent}55` : "#e5e7eb",
                                        background: isActive
                                            ? `linear-gradient(135deg, ${s.accent}0e, #ffffff 70%)`
                                            : "#ffffff",
                                        boxShadow: isActive
                                            ? `0 18px 40px -22px ${s.accent}80`
                                            : "0 1px 2px rgba(30,58,95,0.04)",
                                    }}
                                    data-testid={`story-tab-${s.slug}`}
                                >
                                    <div
                                        className="w-12 h-12 rounded-xl grid place-items-center shrink-0 text-xl font-semibold font-serif transition-transform duration-300 group-hover:scale-105"
                                        style={{
                                            background: `linear-gradient(140deg, ${s.accent}22, ${s.accent}0a)`,
                                            color: s.accent,
                                            border: `1px solid ${s.accent}33`,
                                        }}
                                    >
                                        {s.logoLetter}
                                    </div>
                                    <div className="min-w-0">
                                        <div
                                            className={`font-medium leading-snug truncate transition-colors ${
                                                isActive ? "text-slate-900" : "text-slate-600"
                                            }`}
                                        >
                                            {s.name}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1 inline-flex items-center gap-1.5">
                                            <i className={`${s.channelIcon} text-[10px]`} style={{ color: s.accent }} />
                                            {s.channel}
                                        </div>
                                    </div>
                                    {isActive && (
                                        <motion.span
                                            layoutId="story-tab-glow"
                                            className="absolute -bottom-px left-5 right-5 h-[2px] rounded-full"
                                            style={{ background: s.accent }}
                                        />
                                    )}
                                </button>
                            );
                        })}
                    </div>
                </Reveal>

                {/* ---- Spotlight panel ---- */}
                <div
                    className="rounded-[2rem] overflow-hidden border shadow-sm"
                    style={{ borderColor: `${c.accent}22`, "--accent": c.accent }}
                >
                    <AnimatePresence mode="wait">
                        <motion.div
                            key={c.slug}
                            initial={{ opacity: 0, y: 18 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -12 }}
                            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
                            className="grid lg:grid-cols-12"
                            data-testid={`story-panel-${c.slug}`}
                        >
                            {/* LEFT — narrative (light) */}
                            <div className="lg:col-span-7 bg-white p-8 md:p-11">
                                <div className="flex items-center gap-2 mb-6">
                                    <span
                                        className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-[11px] font-semibold"
                                        style={{ background: `${c.accent}12`, color: c.accent }}
                                    >
                                        <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: c.accent }} />
                                        {c.industry}
                                    </span>
                                </div>

                                <p className="text-slate-500 text-sm md:text-[15px] leading-relaxed mb-8">
                                    {c.background}
                                </p>

                                {/* Challenge */}
                                <div className="mb-8">
                                    <div className="flex items-center gap-2.5 mb-3">
                                        <span
                                            className="w-7 h-7 rounded-lg grid place-items-center shrink-0"
                                            style={{ background: `${c.accent}14`, color: c.accent }}
                                        >
                                            <i className="fa-solid fa-bullseye text-xs" />
                                        </span>
                                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            The challenge
                                        </div>
                                    </div>
                                    <p className="text-slate-800 text-sm md:text-base leading-relaxed pl-9">
                                        {c.challenge}
                                    </p>
                                </div>

                                {/* Solution */}
                                <div>
                                    <div className="flex items-center gap-2.5 mb-4">
                                        <span
                                            className="w-7 h-7 rounded-lg grid place-items-center shrink-0"
                                            style={{ background: `${c.accent}14`, color: c.accent }}
                                        >
                                            <i className="fa-solid fa-wand-magic-sparkles text-xs" />
                                        </span>
                                        <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">
                                            What Nyra delivered
                                        </div>
                                    </div>
                                    <div className="grid sm:grid-cols-2 gap-x-6 gap-y-3 pl-9">
                                        {c.solution.map((s, i) => (
                                            <motion.div
                                                key={s}
                                                initial={{ opacity: 0, x: -8 }}
                                                animate={{ opacity: 1, x: 0 }}
                                                transition={{ delay: 0.15 + i * 0.07, duration: 0.35 }}
                                                className="flex items-start gap-3"
                                            >
                                                <span
                                                    className="w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5"
                                                    style={{ background: `${c.accent}18`, color: c.accent }}
                                                >
                                                    <i className="fa-solid fa-check text-[9px]" />
                                                </span>
                                                <span className="text-sm text-slate-700 leading-relaxed">{s}</span>
                                            </motion.div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                            {/* RIGHT — impact + quote (dark accent) */}
                            <div
                                className="lg:col-span-5 relative overflow-hidden p-8 md:p-11 flex flex-col"
                                style={{
                                    background: `linear-gradient(155deg, #0E2A23 0%, ${c.accent} 165%)`,
                                }}
                            >
                                {/* watermark */}
                                <div
                                    aria-hidden
                                    className="absolute -bottom-10 -right-4 font-serif font-bold pointer-events-none select-none leading-none text-white/5"
                                    style={{ fontSize: "13rem" }}
                                >
                                    {c.logoLetter}
                                </div>

                                <div className="relative flex-1">
                                    <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-white/60 mb-5">
                                        The impact
                                    </div>
                                    <div className="grid grid-cols-2 gap-5">
                                        {c.outcomes.map((o, i) => (
                                            <motion.div
                                                key={o.label}
                                                initial={{ opacity: 0, y: 12 }}
                                                animate={{ opacity: 1, y: 0 }}
                                                transition={{ delay: 0.2 + i * 0.08, duration: 0.4 }}
                                                className="rounded-2xl p-4 bg-white/[0.06] border border-white/10 backdrop-blur-sm"
                                            >
                                                <div className="font-serif text-white text-lg md:text-xl leading-tight">
                                                    {o.value}
                                                </div>
                                                <div className="text-[11px] text-white/55 mt-1.5">{o.label}</div>
                                            </motion.div>
                                        ))}
                                    </div>

                                    {/* Quote */}
                                    <div className="relative mt-8 pl-5">
                                        <span
                                            className="absolute -top-3 left-0 font-serif leading-none select-none text-white/25"
                                            style={{ fontSize: "3rem" }}
                                            aria-hidden
                                        >
                                            &ldquo;
                                        </span>
                                        <div className="absolute top-0 bottom-0 left-0 w-[2px] rounded-full bg-white/25" />
                                        <blockquote className="italic text-white/90 text-sm md:text-[15px] leading-relaxed">
                                            {c.quote}
                                        </blockquote>
                                    </div>
                                </div>

                                {/* Footer CTA */}
                                <div className="relative mt-8 pt-6 border-t border-white/10 flex items-center justify-between gap-3">
                                    <div className="text-xs text-white/60 inline-flex items-center gap-1.5">
                                        Powered by <span className="font-semibold text-white">{c.product}</span>
                                    </div>
                                    <button
                                        onClick={openDemo}
                                        className="text-xs font-semibold inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-white text-slate-900 transition-all hover:gap-2.5 hover:shadow-lg"
                                        data-testid={`story-cta-${c.slug}`}
                                    >
                                        Talk to sales
                                        <i className="fa-solid fa-arrow-right text-[10px]" />
                                    </button>
                                </div>
                            </div>
                        </motion.div>
                    </AnimatePresence>
                </div>
            </div>
        </section>
    );
};

/* ------------------------------------------------------------------
   STORY CARD — gradient product + big stat + Read story
------------------------------------------------------------------- */
export const StoryCard = ({ openDemo }) => (
    <section className="py-16 md:py-20 bg-warm" data-testid="story-card">
        <div className="container-x">
            <Reveal>
                <div className="grid md:grid-cols-5 gap-0 rounded-3xl overflow-hidden gleap-card" style={{ padding: 0 }}>
                    {/* LEFT gradient */}
                    <div className="md:col-span-2 mesh-frame min-h-[280px] flex items-center justify-center" style={{ borderRadius: 0 }}>
                        <div className="glass-card px-6 py-4">
                            <div
                                className="text-lg font-semibold tracking-wider text-slate-900"
                                style={{ letterSpacing: "0.05em" }}
                            >
                                ◆ salesforge
                            </div>
                        </div>
                    </div>
                    {/* RIGHT text */}
                    <div className="md:col-span-3 p-10 flex flex-col justify-center bg-warm-2">
                        <div className="text-slate-900 font-medium mb-2">
                            NyraAI brought support, roadmap, news, and chat into one place.
                        </div>
                        <p className="text-slate-500 text-sm mb-6">
                            NyraAI has everything in one place; that is the biggest benefit.
                        </p>
                        <div className="flex items-end justify-between mt-2">
                            <div>
                                <span
                                    className="heading-stat text-5xl text-slate-900"
                                >
                                    &lt;2 min
                                </span>
                                <div className="text-xs text-slate-500 mt-1">Response SLA on 60–70 daily tickets</div>
                            </div>
                            <button onClick={openDemo} className="pill-dark" data-testid="story-read-btn">
                                Read story
                            </button>
                        </div>
                    </div>
                </div>
            </Reveal>
        </div>
    </section>
);

/* ------------------------------------------------------------------
   FAQ ACCORDION
------------------------------------------------------------------- */
